/**
 * The parameters for filtering out specific stock counters of the matched skus on their enabled API channels:
 *
 * |Parameter|Description|Example|
 * |:--------|:----------|:------|
 * |`schema_version`|A string of the schema version to be used.|`schema_version=2018-09-25`|
 * |`sku_active`|A boolean value of truth for searching a list of active skus. An active sku is one that is PCS published, usable and non-dummy. This parameter is interpreted on the `admin`, `app` and `pos` channels. For other API channels or absence of the parameter, `sku_active` is set to true.|`sku_active=false`|
 * |`distinct`|A string for searching a list of stock counters with the specified `distinct` parameter. Different values of `distinct` produce different stock counters, as stated below:<br>- `<null string>``: Return a list of stock counters of disinct skus.<br>- `p`: Return a list of stock counters of the skus with distinct products.<br>- `pc`: Return a list of stock counters of the skus with distinct products and color options combination.|`distinct=pc`|
 * |`store_code_eq`|An exact-matching string for searching a list of stock counters of the skus in the store specified by the store code.|`store_code_eq`=`cwb`|
 * |`store_ids[]`|An array of integers for searching a list of stock counters of the skus in the stores specified by the store ids.|`store_ids[]=4`|
 * |`product_code_eq`|An exact-matching string for searching a list of stock counters of the skus specified by the product code.|`product_code_eq=P123456`|
 * |`product_ids[]`|An array of integers for searching a list of stock counters of the skus specified by the product ids.|`product_ids[]=2`|
 * |`color_master_code_eq`|An exact-matching string for searching a list of stock counters of the skus with the color option id where the color option is having the specified color master code.|`color_master_code_eq=BLACK`|
 * |`color_master_ids[]`|An array of integers for searching a list of stock counters of the skus with the color option ids where the color options are having the specified color master ids.|`color_master_ids[]=2`|
 * |`color_option_code_eq`|An exact-matching string for searching a list of stock counters of the skus specified by the color option code.|`color_option_code_eq=HK000123`|
 * |`color_option_ids[]`|An array of integers for searching a list of stock counters of the skus specified by the color option ids.|`color_option_ids[]=23`|
 * |`size_master_code_eq`|An exact-matching string for searching a list of stock counter of the skus with the size option id where the size option is having the specified size master code.|`size_master_code_eq=LARGE`|
 * |`size_master_ids[]`|An array of integers for searching a list of stock counters of the skus with the size option ids where the size options are having the specified size master ids.|`size_master_ids[]=11`|
 * |`size_option_code_eq`|An exact-matching string for searching a list of stock counters of the skus specified by the size option code.|`size_option_code_eq`=`0086001`|
 * |`size_option_ids[]`|An array of integers for searching a list of stock counters of the skus specified by the size option ids.|`size_option_ids[]`=`36000`|
 * |`upc_code_eq`|An exact-matching string for searching a list of stock counters of the skus with the specified upc code.|`upc_code_eq=3145891055108`|
 * |`sku_ids[]`|An array of integers for searching a list of stock counters of the skus specified by the sku ids.|`sku_ids[]=23`|
 * |`sku_code_eq`|An exact-matching string for searching a list of stock counters of the skus with the specified sku code.|`sku_code_eq=0105510`|
 * |`page_size`|An integer for the number of records per page. Default page size is 25.|`page_size=20`|
 * |`page`|An integer for the currently displayed page number.|`page=2`|
 *
 */

import _ from 'lodash'
import { CancelToken } from 'axios'
import { OmnitechApi } from '../../api'
import { cancelRequest } from '../../helpers'
import { isErrorExist, convertResponseToErrorObject } from '../../omnitech-error'

export function fetchStockCounters(options = {}) {
  const { page = 1, ...others } = options
  const params = {
    page,
    ...others
  }
  const source = CancelToken.source()
  cancelRequest.add(
    'fetchStockCounters',
    () => source.cancel('fetchStockCounters: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: 'stock_counters',
      method: 'get',
      params,
      cancelToken: source.token,
      arrayFormat: 'brackets'

    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      stockCounters: data.stockCounters,
      pagination: data.stockCountersPagination,
      next: _.get(data, 'stockCountersPagination.nextPage') > page
        ? () =>
          fetchStockCounters({
            page: page + 1,
            ...others
          })
        : null,
      count: _.get(data, 'stockCountersPagination.totalCount')
    }))
}
